import { useState } from 'react';
import { ArrowRight, Check, Shield, Clock, Users } from 'lucide-react';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { openChat } from '@/utils/chatService';

const ServiceTemplate = ({ 
  title, 
  subtitle, 
  heroImage, 
  overviewImage,
  overviewTitle,
  description, 
  benefits, 
  process, 
  faqs,
  whyChoose,
  whoNeedsThis,
  whatYouGet,
  showPricing = false 
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Use overviewImage if provided, otherwise use a different default image
  const displayOverviewImage = overviewImage || 'https://images.unsplash.com/photo-1739285452621-59d92842fcc8?w=1200&q=80';
  const displayOverviewTitle = overviewTitle || 'Overview';

  return (
    <>
      <SEO 
        title={`${title} - Professional ${subtitle || 'Legal Services'}`}
        description={
          typeof description === 'string' 
            ? description.substring(0, 155)
            : Array.isArray(description)
            ? description[0]?.substring(0, 155)
            : `Expert ${title.toLowerCase()} services. Professional legal assistance with proven results. Get started today with free consultation.`
        }
        keywords={`${title.toLowerCase()}, ${subtitle?.toLowerCase() || 'legal services'}, trademark registration, IP protection`}
        ogImage={overviewImage || heroImage}
      />
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="service-hero-section">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        {/* Dynamic Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-red-600/20 to-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-orange-600/15 to-red-500/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Centered Content */}
            <div className="space-y-6 text-white">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                {title}
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                {subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3 rounded-full text-lg font-semibold hover:shadow-2xl"
                  data-testid="service-hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => openChat()}
                  variant="outline"
                  className="border-2 border-white text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white/10"
                  data-testid="service-hero-chat-now-btn"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section - Image on Left, Content on Right */}
      <section className="py-20 bg-white" data-testid="service-description-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="order-2 md:order-1">
              <img
                src={displayOverviewImage}
                alt={displayOverviewTitle}
                className="rounded-3xl shadow-2xl w-full h-auto"
              />
            </div>
            {/* Right Side - Content */}
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-8">{displayOverviewTitle}</h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                {description.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Consistent Alignment */}
      {benefits && benefits.length > 0 && (
        <section className="py-20 bg-gray-50" data-testid="service-benefits-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Key Benefits</h2>
              <p className="text-xl text-gray-600">Why choose our services</p>
            </div>

            <MobileSlider>
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl card-hover shadow-lg h-full"
                  data-testid={`benefit-${idx}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </MobileSlider>
          </div>
        </section>
      )}

      {/* Process Section - Matching Homepage Style - Consistent Alignment */}
      {process && process.length > 0 && (
        <section className="py-20 bg-gray-50" data-testid="service-process-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Simple Process</h2>
              <p className="text-xl text-gray-600">From consultation to completion - we handle everything</p>
            </div>

            {/* Desktop View - Grid Layout like Homepage */}
            <div className="hidden md:grid gap-8" style={{ gridTemplateColumns: `repeat(${Math.min(process.length, 4)}, 1fr)` }}>
              {process.map((step, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-8 text-center card-hover"
                  data-testid={`process-step-${idx}`}
                >
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-3xl text-white font-bold">{idx + 1}</span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <span className="inline-block bg-red-100 text-red-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                      Step {idx + 1}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>

            {/* Mobile View - Horizontal Slider */}
            <div className="md:hidden">
              <MobileSlider cols={process.length > 3 ? 4 : 3}>
                {process.map((step, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 text-center shadow-lg h-full"
                    data-testid={`process-step-mobile-${idx}`}
                  >
                    <div className="flex justify-center mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-3xl text-white font-bold">{idx + 1}</span>
                      </div>
                    </div>
                    <div className="mb-4">
                      <span className="inline-block bg-red-100 text-red-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                        Step {idx + 1}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </MobileSlider>
            </div>
          </div>
        </section>
      )}

      {/* Section 1: Why Choose This Service */}
      {whyChoose && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-testid="why-choose-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{whyChoose.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{whyChoose.subtitle}</p>
            </div>

            <MobileSlider>
              {whyChoose.reasons.map((reason, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow h-full"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                    <span className="text-3xl">{reason.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-center">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-center">{reason.description}</p>
                </div>
              ))}
            </MobileSlider>
          </div>
        </section>
      )}

      {/* Section 2: Who Needs This Service */}
      {whoNeedsThis && (
        <section className="py-20 bg-white" data-testid="who-needs-section">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <img
                  src={whoNeedsThis.image || 'https://images.unsplash.com/photo-1714974528704-befac88c2fd6?w=1200&q=80'}
                  alt={whoNeedsThis.title}
                  className="rounded-3xl shadow-2xl w-full h-full object-cover"
                  style={{ minHeight: '400px', maxHeight: '500px' }}
                />
              </div>
              <div className="space-y-6 order-1 md:order-2">
                <h2 className="text-4xl md:text-5xl font-bold leading-tight">{whoNeedsThis.title}</h2>
                <p className="text-xl text-gray-700 leading-relaxed">{whoNeedsThis.description}</p>
                
                <div className="space-y-4">
                  {whoNeedsThis.examples.map((example, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mt-1">
                        <Check className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{example.title}</h4>
                        <p className="text-gray-600">{example.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 3: What You'll Get */}
      {whatYouGet && (
        <section className="py-20 bg-gradient-to-br from-red-50 via-orange-50 to-red-50" data-testid="what-you-get-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{whatYouGet.title}</h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">{whatYouGet.subtitle}</p>
            </div>

            <MobileSlider>
              {whatYouGet.deliverables.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 shadow-lg h-full"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">{item.icon}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </MobileSlider>
          </div>
        </section>
      )}

      {/* Testimonials & FAQs Combined */}
      <TestimonialFAQSection faqs={faqs} />

      {/* CTA Section - Strong Call to Action with Background */}
      <section className="relative py-24 overflow-hidden" data-testid="service-cta-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1758599543278-32d9d073941e?w=1920&q=80" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/95"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Don't Wait—Protect Your Brand Before Someone Else Takes It
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              Every day without trademark protection puts your business at risk.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Join 5,000+ businesses that chose Easy Trademark Filing for fast, affordable, and reliable brand protection. 
              Get expert legal support and the confidence that your intellectual property is secure.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-6 rounded-full text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="service-cta-get-started-btn"
              >
                Start Your Trademark Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => setIsPopupOpen(true)}
                variant="outline"
                className="border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-slate-900 transition-all"
                data-testid="service-cta-consultation-btn"
              >
                Get Free Consultation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                <span className="text-sm">98% Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-sm">Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-red-400" />
                <span className="text-sm">5,000+ Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default ServiceTemplate;